import React, { createContext, useContext, useState, ReactNode } from 'react'
import { trpc } from '../utils'
import { useSnackbar } from 'notistack'
import { StoredDocument, SigningParams } from '../types'

interface StoredDocumentContextType {
  documents: StoredDocument[]
  loading: boolean
  signAndStoreDocuments: (
    files: File[],
    signingParams: SigningParams,
  ) => Promise<void>
  downloadDocument: (documentId: string) => Promise<void>
  deleteDocument: (documentId: string) => Promise<void>
  deleteAllDocuments: () => Promise<void>
  refreshDocuments: () => Promise<void>
  signingLoading: boolean
  signingProgress: {
    total: number
    completed: number
    failed: number
  }
}

const StoredDocumentContext = createContext<
  StoredDocumentContextType | undefined
>(undefined)

export const StoredDocumentProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [documents, setDocuments] = useState<StoredDocument[]>([])
  const [loading, setLoading] = useState(false)
  const [signingLoading, setSigningLoading] = useState(false)
  const [signingProgress, setSigningProgress] = useState({
    total: 0,
    completed: 0,
    failed: 0,
  })
  const { enqueueSnackbar } = useSnackbar()

  const refreshDocuments = async () => {
    setLoading(true)
    try {
      const result = await trpc.getUserDocuments.query()
      if (result.success) {
        setDocuments(result.documents)
      }
    } catch (error) {
      console.error('Error fetching documents:', error)
      enqueueSnackbar('Evraklar yüklenirken hata oluştu', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const signAndStoreDocuments = async (
    files: File[],
    signingParams: SigningParams,
  ) => {
    if (files.length === 0) {
      enqueueSnackbar('Lütfen en az bir dosya seçin', { variant: 'warning' })
      return
    }

    setSigningLoading(true)
    setSigningProgress({
      total: files.length,
      completed: 0,
      failed: 0,
    })

    for (const file of files) {
      try {
        const fileBuffer = await file.arrayBuffer()
        const fileBase64 = Buffer.from(fileBuffer).toString('base64')

        const result = await trpc.signAndStoreDocument.mutate({
          udfFileBase64: fileBase64,
          fileName: file.name,
          ...signingParams,
        })

        if (result.success) {
          setSigningProgress((prev) => ({
            ...prev,
            completed: prev.completed + 1,
          }))
          enqueueSnackbar(`${file.name} başarıyla imzalandı`, {
            variant: 'success',
          })
        } else {
          setSigningProgress((prev) => ({
            ...prev,
            failed: prev.failed + 1,
          }))
          enqueueSnackbar(`${file.name} imzalanırken hata: ${result.message}`, {
            variant: 'error',
          })
        }
      } catch (error) {
        console.error('Error signing document:', error)
        setSigningProgress((prev) => ({
          ...prev,
          failed: prev.failed + 1,
        }))
        enqueueSnackbar(`${file.name} imzalanırken hata oluştu`, {
          variant: 'error',
        })
      }
    }

    setSigningLoading(false)
    await refreshDocuments()
  }

  const downloadDocument = async (documentId: string) => {
    try {
      const result = await trpc.downloadDocument.mutate({ documentId })

      if (result.success) {
        const document = documents.find((d) => d.id === documentId)
        if (!document) return

        const blob = new Blob([Buffer.from(result.fileBase64, 'base64')])
        const url = URL.createObjectURL(blob)
        const a = window.document.createElement('a')
        a.href = url
        a.download = `${(document.metadata.signerName || '')
          .split(' ')
          .map((item) => item[0])
          .join('')}.${document.signedFileName}`
        window.document.body.appendChild(a)
        a.click()
        window.document.body.removeChild(a)
        URL.revokeObjectURL(url)

        enqueueSnackbar('Dosya indirildi', { variant: 'success' })
      }
    } catch (error) {
      console.error('Error downloading document:', error)
      enqueueSnackbar('Dosya indirilirken hata oluştu', { variant: 'error' })
    }
  }

  const deleteDocument = async (documentId: string) => {
    try {
      const result = await trpc.deleteDocument.mutate({ documentId })

      if (result.success) {
        enqueueSnackbar('Evrak silindi', { variant: 'success' })
        await refreshDocuments()
      }
    } catch (error) {
      console.error('Error deleting document:', error)
      enqueueSnackbar('Evrak silinirken hata oluştu', { variant: 'error' })
    }
  }

  const deleteAllDocuments = async () => {
    try {
      const result = await trpc.deleteAllDocuments.mutate()

      if (result.success) {
        enqueueSnackbar('Tüm evraklar silindi', { variant: 'success' })
        await refreshDocuments()
      }
    } catch (error) {
      console.error('Error deleting all documents:', error)
      enqueueSnackbar('Evraklar silinirken hata oluştu', { variant: 'error' })
    }
  }

  const contextValue: StoredDocumentContextType = {
    documents,
    loading,
    signAndStoreDocuments,
    downloadDocument,
    deleteDocument,
    deleteAllDocuments,
    refreshDocuments,
    signingLoading,
    signingProgress,
  }

  return (
    <StoredDocumentContext.Provider value={contextValue}>
      {children}
    </StoredDocumentContext.Provider>
  )
}

export const useStoredDocumentContext = () => {
  const context = useContext(StoredDocumentContext)
  if (context === undefined) {
    throw new Error(
      'useStoredDocumentContext must be used within a StoredDocumentProvider',
    )
  }
  return context
}
